import { Box, Card, Grid, ListSubheader, Typography } from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import { Contract, ContractContractType } from "../model/Contract";

import { AppContext } from "../context/AppContext";
import translate from "../i18n/Translator";

import { getDocumentUrl } from "../api/TenantContractAPI";
import { formatDate } from "../components/DateFormat";
import { ErrorSnackbar } from "../components/Snackbars";

export interface ContractDetailsParams {
    contract: Contract;
    queryStr: string;
    referer?: string;
}

interface ContractInfoItem {
    value: React.ReactNode;
    label: React.ReactNode;
}

export function infoItem({ value, label }: ContractInfoItem) {
    return (
        <Grid item xs={6} md={4}>
            <Box p={2}>
                <Typography variant="body2" color="textPrimary">
                    {value}
                </Typography>
                <Typography variant="body2" style={{ color: "gray" }} color="textSecondary">
                    {label}
                </Typography>
            </Box>
        </Grid>
    );
}

export default function ContractDetails(props: ContractDetailsParams) {
    const context = useContext(AppContext);
    const tenantId = context.session?.tenant?.id;

    const [documentUrl, setDocumentUrl] = useState<string>("");

    const [error, setError] = useState<string | JSX.Element | JSX.Element[]>();

    const getTypeLabel = (type: ContractContractType) => translate(`contracts.type.${type}`) as string;

    useEffect(() => {
        if (!tenantId) return;
        getDocumentUrl(tenantId, props.contract.id)
            .then((url) => {
                setDocumentUrl(url);
            })
            .catch((error) => {
                setError(error);
            });
    }, [props.contract.id, tenantId]);

    const onClosedSnackbar = () => {
        setError("");
    };

    return (
        <Box py={2} px={2}>
            <Grid container justifyContent="space-between">
                <Grid item xs={12} spacing={10}>
                    <Card variant="outlined">
                        <Grid container>
                            <Grid item xs={12}>
                                <ListSubheader style={{ textTransform: "uppercase" }}>
                                    {translate(`se_contracts.view.info`)}
                                </ListSubheader>
                            </Grid>
                            {infoItem({
                                value: props.contract.description,
                                label: translate("contracts.description"),
                            })}
                            {infoItem({
                                value: props.contract.provider?.name || "",
                                label: translate("contracts.provider"),
                            })}
                            {infoItem({ value: props.contract.sisub, label: translate("contracts.sisub") })}
                            {infoItem({
                                value: getTypeLabel(props.contract.type),
                                label: translate("contracts.type"),
                            })}
                            {infoItem({
                                value: (
                                    <a href={documentUrl} target="_blank" rel="noopener noreferrer">
                                        {props.contract.document.split("/").pop()}
                                    </a>
                                ),
                                label: translate("contracts.document"),
                            })}
                            {infoItem({
                                value: props.contract.icsoe,
                                label: translate("contracts.icsoe"),
                            })}
                            {infoItem({
                                value: formatDate(props.contract.created_at, "DD/MM/YYYY"),
                                label: translate("contracts.date_created"),
                            })}
                            {infoItem({
                                value: formatDate(props.contract.specialized_services?.from, "DD/MM/YYYY"),
                                label: translate("contracts.from"),
                            })}
                            {infoItem({
                                value: props.contract.specialized_services?.date_undetermined
                                    ? translate("se_contracts.view.undefined")
                                    : formatDate(props.contract.specialized_services?.to, "DD/MM/YYYY"),
                                label: translate("contracts.to"),
                            })}
                        </Grid>
                    </Card>
                </Grid>
            </Grid>
            <ErrorSnackbar message={error} onClose={onClosedSnackbar} />
        </Box>
    );
}
